import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logSecurityEvent } from '@/utils/securityUtils';
import { toast } from 'sonner';

interface SessionTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  extendOnActivity?: boolean;
}

export const useSessionTimeout = (options: SessionTimeoutOptions = {}) => {
  const {
    timeoutMinutes = 30, // 30 minutes default
    warningMinutes = 5,  // 5 minutes warning
    extendOnActivity = true
  } = options;

  const { user, signOut } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const warningRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());

  const handleSessionTimeout = useCallback(async () => {
    if (user) {
      await logSecurityEvent({
        event_type: 'api_access',
        endpoint: 'session_timeout',
        details: `Session timeout for user: ${user.email}`,
        severity: 'low'
      });
      
      toast.error('Session expired. Please sign in again.');
      await signOut();
    }
  }, [user, signOut]);

  const handleSessionWarning = useCallback(() => {
    if (user) {
      toast.warning(`Your session will expire in ${warningMinutes} minutes due to inactivity.`, {
        duration: 10000,
        action: {
          label: 'Extend Session',
          onClick: () => {
            resetTimeout();
            toast.success('Session extended');
          }
        }
      });
    }
  }, [user, warningMinutes]);

  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!user) return;

    lastActivityRef.current = Date.now();

    // Set warning timeout
    warningRef.current = setTimeout(
      handleSessionWarning,
      (timeoutMinutes - warningMinutes) * 60 * 1000
    );

    // Set session timeout
    timeoutRef.current = setTimeout(
      handleSessionTimeout,
      timeoutMinutes * 60 * 1000
    );
  }, [user, timeoutMinutes, warningMinutes, handleSessionWarning, handleSessionTimeout]);

  const handleActivity = useCallback(() => {
    if (!extendOnActivity || !user) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Only reset if it's been more than 1 minute since last activity
    if (timeSinceLastActivity > 60000) {
      resetTimeout();
    }
  }, [extendOnActivity, user, resetTimeout]);

  useEffect(() => {
    if (user) {
      resetTimeout();

      if (extendOnActivity) {
        // Add activity listeners
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        events.forEach(event => {
          document.addEventListener(event, handleActivity, { passive: true });
        });

        return () => {
          events.forEach(event => {
            document.removeEventListener(event, handleActivity);
          });
        };
      }
    } else {
      // Clear timeouts when user signs out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, extendOnActivity, resetTimeout, handleActivity]);

  // Check token expiration periodically
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiration = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          await handleSessionTimeout();
          return;
        }

        // Check if token expires in the next 5 minutes
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
        const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
        
        if (expiresAt < fiveMinutesFromNow) {
          // Try to refresh the session
          const { error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            await logSecurityEvent({
              event_type: 'auth_failure',
              details: `Failed to refresh session for user: ${user.email}`,
              severity: 'medium'
            });
            await handleSessionTimeout();
          }
        }
      } catch (error) {
        console.warn('Error checking token expiration:', error);
      }
    };

    // Check token every minute
    const tokenCheckInterval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(tokenCheckInterval);
  }, [user, handleSessionTimeout]);

  return {
    resetTimeout,
    lastActivity: lastActivityRef.current
  };
};